import {useCallback, useEffect, useRef, useState} from "react";
import {useInterval} from "./useInterval";


export const useTypewriter = (props = {}) => {
  const {delay = 25, initialText = '', initialOffset = 50, emitWords = true} = props || {};
  const [completionCallback, setCompletionCallback] = useState(null);
  const [fullText, setFullText] = useState(initialText);
  const [offset, setOffset] = useState(initialOffset);
  const [displayText, setDisplayText] = useState(fullText.substring(0, offset))
  const isActive = displayText !== fullText;

  useEffect(() => {
    setDisplayText(fullText.slice(0, offset));
  }, [offset, fullText]);

  useEffect(() => {
    if (offset === fullText.length) {
      if (typeof completionCallback === "function") {
        completionCallback(fullText);
      }
      setCompletionCallback(null);
    }
  }, [offset, fullText, completionCallback]);

  useInterval(() => {
    const ch = fullText.charAt(offset);
    if (!emitWords || ch === ' ') {
      setOffset(offset => offset + 1);
    } else {
      const sub = fullText.substring(offset);
      const next = sub.indexOf(" ");
      if (next === -1) {
        setOffset(offset => offset + sub.length);
      } else {
        setOffset(offset => offset +  next + 1);
      }
    }
  }, isActive ? delay : null);

  const startTyping = (text, onTypingComplete = null) => {
    setCompletionCallback(() => onTypingComplete);
    setFullText(text);
    setOffset(initialOffset);
  };
  return [displayText, startTyping];
}

const TYPEWRITER_MIN_DELAY = 8;   // ms — fastest allowed (125 chars/sec)
const TYPEWRITER_MAX_DELAY = 50;  // ms — slowest allowed (20 chars/sec)
const TYPEWRITER_DEFAULT_DELAY = 20; // ms starting estimate
// DOM writes are throttled to this interval regardless of char delay, to limit
// rrweb DOM mutation frequency (PostHog session recording rate-limit avoidance)
// and the frequency of full markdown recompiles — each write re-renders
// MemoizedMarkdown, which re-parses the ENTIRE accumulated text (markdown-to-jsx
// has no incremental re-parse).
const TYPEWRITER_DOM_WRITE_INTERVAL = 50; // ms (~20 renders/sec)

/**
 * Typewriter effect for streaming text that grows incrementally.
 *
 * Unlike useTypewriter (which animates a fixed string), this hook chases
 * a continuously-growing target text. It self-tunes charDelay by measuring
 * inter-chunk intervals and chunk sizes from the live stream, so the
 * animation keeps pace with the actual server output rate.
 *
 * When streaming ends (active=false), display jumps immediately to full text.
 *
 * @param {string} targetText - The full text received so far (grows over time)
 * @param {boolean} active - true while streaming; false when complete
 * @returns {string} The portion of targetText that should be displayed
 */
export const useStreamingTypewriter = (targetText, active, { drainOnComplete = false } = {}) => {
  const posRef = useRef(0);
  const [displayText, setDisplayText] = useState('');
  const delayRef = useRef(TYPEWRITER_DEFAULT_DELAY);
  const lastDomWriteRef = useRef(0);

  // Track recent chunks to estimate chars/ms rate
  const prevLenRef = useRef(0);
  const prevTimeRef = useRef(null);

  // Measure chunk rate whenever targetText grows
  useEffect(() => {
    if (!active || !targetText) return;
    const now = Date.now();
    const newChars = targetText.length - prevLenRef.current;
    if (newChars > 0 && prevTimeRef.current !== null) {
      const elapsed = now - prevTimeRef.current;
      if (elapsed > 0) {
        const estimated = elapsed / newChars;
        delayRef.current = Math.min(TYPEWRITER_MAX_DELAY, Math.max(TYPEWRITER_MIN_DELAY, estimated));
      }
    }
    prevLenRef.current = targetText.length;
    prevTimeRef.current = now;
  }, [targetText, active]);

  // When streaming ends: jump to full text unless drainOnComplete is set
  useEffect(() => {
    if (!active && targetText && !drainOnComplete) {
      posRef.current = targetText.length;
      setDisplayText(targetText);
    }
  }, [active, targetText, drainOnComplete]);

  // Reset when a new stream starts (targetText shrinks or clears)
  useEffect(() => {
    if (active && targetText != null && targetText.length < posRef.current) {
      posRef.current = 0;
      prevLenRef.current = 0;
      prevTimeRef.current = null;
      lastDomWriteRef.current = 0;
      delayRef.current = TYPEWRITER_DEFAULT_DELAY;
      setDisplayText('');
    }
  }, [active, targetText]);

  // When draining post-stream, use fastest delay to catch up quickly
  const charsRemaining = (targetText?.length ?? 0) - posRef.current;
  const isDraining = drainOnComplete && !active && charsRemaining > 0;
  const intervalDelay = active && posRef.current < (targetText?.length ?? 0)
    ? delayRef.current
    : (isDraining ? TYPEWRITER_MIN_DELAY : null);

  useInterval(() => {
    if (!targetText || posRef.current >= targetText.length) return;
    posRef.current += 1;
    const now = Date.now();
    const caught_up = posRef.current >= targetText.length;
    if (caught_up || now - lastDomWriteRef.current >= TYPEWRITER_DOM_WRITE_INTERVAL) {
      lastDomWriteRef.current = now;
      setDisplayText(targetText.slice(0, posRef.current));
    }
  }, intervalDelay);

  return displayText;
}
